import { html, nothing } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import type { CreatePostParams } from "api-contracts/rpc/create-post";
import type { UpdatePostParams } from "api-contracts/rpc/update-post";
import { SignalElement, inject } from "ui-common/di";
import type { ICreatorProfileDataState } from "../../services/creator-profile-data/types";
import type { IBackofficeWorldsState } from "../../services/backoffice-worlds/types";
import type { IBackofficePostsService } from "../../services/backoffice-posts/types";
import "ui-components/alert-item/alert-item";
import "ui-components/button/wow-button";
import "ui-components/expandible-switch/expandible-switch";
import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/switch";
import "ui-components/modal/wow-modal";
import "ui-components/expand-text/expand-text";
import { NotificationService } from "ui-components/notification/notification-service";
import "ui-components/user-theme-button/user-theme-button";

import { getAssetUrl } from "helpers/assetHelpers";

import { previewPublishModalStyles } from "./preview-publish-modal.css";

import { t, translate } from "../../translations/i18n";
import { thumbnailStyles } from "../create-post-modal/create-post-modal.css";
import { toISODateTime } from "helpers/dateHelpers";

// FAQ block interface
interface FaqBlock {
  id: string;
  question: string;
  answer: string;
}

// Preview modal data interface
export interface PreviewModalData {
  description: string;
  mediaFileId: string;
  mediaFileUrl?: string;
  mediaFileType?: "image" | "video";
  selectedExistingWorldIds: string[];
  selectedNewWorldNames: string[];
  selectedBrandIds: string[];
  ctaData?: { callToAction: string; url: string };
  discountData?: { code: string };
  faqBlocks: FaqBlock[];
  scheduledDateTime?: string;
  switches: {
    ctaButton: boolean;
    discount: boolean;
    brand: boolean;
    faq: boolean;
  };
  postId?: string; // Added to indicate edit mode
  isCurrentlyCover?: boolean; // Added to track current cover status from DB
  premiumTier?: "free" | "premium";
  enableOneTimePurchase?: boolean;
  oneTimePriceCents?: number;
}

@customElement("preview-publish-modal")
export class PreviewPublishModal extends SignalElement {
  static override styles = [previewPublishModalStyles, thumbnailStyles];

  @property({ type: Object })
  previewData!: PreviewModalData;

  @state()
  private schedulePostEnabled = false;

  @state()
  private scheduledDateTime?: string;

  @state()
  private setCoverEnabled = false;

  @inject<IBackofficeWorldsState>("backofficeWorldsState")
  private backofficeWorldsState!: IBackofficeWorldsState;

  @inject<ICreatorProfileDataState>("creatorProfileDataState")
  private creatorProfileDataState!: ICreatorProfileDataState;

  @inject<IBackofficePostsService>("backofficePostsService")
  private backofficePostsService!: IBackofficePostsService;

  private getUserPrimaryColor(): string | undefined {
    const profileData = this.creatorProfileDataState.profile.get();
    if (!profileData) return undefined;

    // Priority: customColors > theme > fallback secondaryColor
    return (
      profileData.theme?.primaryColor || profileData.primaryColor || profileData.customColors?.primary || undefined
    );
  }

  private getUserSecondaryColor(): string | undefined {
    const profileData = this.creatorProfileDataState.profile.get();
    if (!profileData) return undefined;

    // Priority: customColors > theme > fallback secondaryColor
    return (
      profileData.theme?.secondaryColor ||
      profileData.secondaryColor ||
      profileData.customColors?.secondary ||
      undefined
    );
  }

  private handleClose = () => {
    this.dispatchEvent(
      new CustomEvent("close", {
        bubbles: true,
        composed: true,
      }),
    );
  };

  private handleScheduleExpandedChange = (event: CustomEvent) => {
    this.schedulePostEnabled = event.detail.expanded;
  };

  private handleScheduleDateTimeChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.scheduledDateTime = target.value;
  };

  private handleSetCoverChange = (event: CustomEvent) => {
    this.setCoverEnabled = event.detail.checked;
  };

  private getWorldNames(): string[] {
    const worldsData = this.backofficeWorldsState.worlds.get();
    const existingWorldNames = this.previewData.selectedExistingWorldIds
      .map((id) => worldsData?.worlds?.find((w) => w.id === id)?.name)
      .filter(Boolean) as string[];

    return [...existingWorldNames, ...this.previewData.selectedNewWorldNames];
  }

  private handlePublish = async () => {
    const isEditMode = !!this.previewData.postId;

    try {
      const basePostData: Omit<CreatePostParams, "description"> | Omit<UpdatePostParams, "postId" & "teaser"> = {
        worldIds: this.previewData.selectedExistingWorldIds,
        newWorldNames: this.previewData.selectedNewWorldNames,
        brandIds: this.previewData.selectedBrandIds,
        ctaData: this.previewData.switches.ctaButton && this.previewData.ctaData ? this.previewData.ctaData : undefined,
        discountData:
          this.previewData.switches.discount && this.previewData.discountData
            ? this.previewData.discountData
            : undefined,
        faqBlocks: this.previewData.switches.faq ? this.previewData.faqBlocks : [],
        fileId: this.previewData.mediaFileId,
        cover: this.setCoverEnabled,
        startDate: this.schedulePostEnabled && this.scheduledDateTime ? this.scheduledDateTime : toISODateTime(),
        contentTier: this.previewData.premiumTier,
        oneTimePriceCents: this.previewData.enableOneTimePurchase ? this.previewData.oneTimePriceCents : undefined,
      };

      // Use appropriate mutation based on whether we're creating or updating

      console.log("[PREVIEW MODAL] premiumTier value:", this.previewData.premiumTier);
      console.log("[PREVIEW MODAL] Edit mode:", isEditMode);

      const result = isEditMode
        ? await this.backofficePostsService.updatePost({
            postId: this.previewData.postId!,
            teaser: this.previewData.description,
            ...basePostData,
          })
        : await this.backofficePostsService.createPost({
            description: this.previewData.description,
            ...basePostData,
          });
      if (result && result.postId) {
        NotificationService.show({
          message: translate(isEditMode ? "editPost.updateSuccess" : "createPost.createSuccess"),
          severity: "success",
        });

        // Emit success event to close both modals
        this.dispatchEvent(
          new CustomEvent("publish-success", {
            bubbles: true,
            composed: true,
          }),
        );
      } else {
        NotificationService.show({
          message: translate(isEditMode ? "editPost.updateError" : "createPost.createError"),
          severity: "error",
        });
      }
    } catch (error) {
      const actionText = isEditMode ? "updating" : "creating";
      console.error(`Error ${actionText} post:`, error);
      NotificationService.show({
        message: translate(isEditMode ? "editPost.updateError" : "createPost.createError"),
        severity: "error",
      });
    }
  };

  private renderThumbnailPreview() {
    if (!this.previewData.mediaFileUrl) {
      return html`
        <div class="no-thumbnail">
          <ph-image size="48"></ph-image>
          <span>${t("previewPublish.noThumbnail")}</span>
        </div>
      `;
    }

    const mediaUrl = getAssetUrl(this.previewData.mediaFileUrl);
    if (!mediaUrl) {
      return html`
        <div class="no-thumbnail">
          <ph-image size="48"></ph-image>
          <span>${t("previewPublish.noThumbnail")}</span>
        </div>
      `;
    }

    const worldNames = this.getWorldNames();

    return html`
      <div class="thumbnail-preview">
        ${this.previewData.mediaFileType === "video"
          ? html`
              <video
                class="thumbnail-video"
                loop
                muted
                playsinline
                preload="auto"
                src="${mediaUrl}"
                @loadeddata=${(e: Event) => {
                  const video = e.target as HTMLVideoElement;
                  video.play().catch((err) => console.error("Video play failed:", err));
                }}
              >
                Your browser does not support the video tag.
              </video>
            `
          : html`<img src="${mediaUrl}" alt="Post thumbnail" />`}
        <div class="thumbnail-overlay">
          <!-- Worlds at top -->
          ${worldNames.length > 0 ? html` <div class="worlds-text">${worldNames.join(", ")}</div> ` : ""}

          <!-- Bottom content block -->
          <div class="overlay-content">
            ${this.previewData.description
              ? html`
                  <expand-text class="overlay-description" no-more disabled
                    >${this.previewData.description}</expand-text
                  >
                `
              : nothing}
            ${this.previewData.faqBlocks.length > 0
              ? html` <div class="overlay-faq-text">${t("previewPublish.frequentlyAskedQuestions")}</div> `
              : nothing}
            ${this.previewData.switches.ctaButton && this.previewData.ctaData
              ? html`
                  <user-theme-button
                    class="cta-button"
                    size="small"
                    full-width
                    .primaryColor=${this.getUserPrimaryColor()}
                    .secondaryColor=${this.getUserSecondaryColor()}
                    .interactive=${false}
                    >${this.previewData.ctaData.callToAction}</user-theme-button
                  >
                `
              : nothing}
          </div>
        </div>
      </div>
    `;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.schedulePostEnabled =
      this.previewData.scheduledDateTime != undefined && this.previewData.scheduledDateTime != "";
    this.scheduledDateTime = this.previewData.scheduledDateTime?.substring(0, 16);

    this.setCoverEnabled = (this.previewData.postId && this.previewData.isCurrentlyCover) || false;
  }

  protected override render() {
    return html`
      <wow-modal
        mobile-fullscreen
        variant="responsive"
        @close=${this.handleClose}
        @footer-button-clicked=${this.handlePublish}
      >
        <span slot="header">${t("previewPublish.title")}</span>
        <span slot="footer-button-text">${t("previewPublish.publishPost")}</span>

        <div class="preview-container">
          <h2 class="preview-title">${t("previewPublish.previewTitle")}</h2>

          <div class="preview-content">
            ${this.renderThumbnailPreview()}

            <!-- Schedule Post Switch -->
            <expandible-switch
              ?isExpanded=${this.previewData.scheduledDateTime != undefined && this.previewData.scheduledDateTime != ""}
              @expandedChange=${this.handleScheduleExpandedChange}
              label="${t("previewPublish.schedulePost")}"
            >
              <div slot="content" class="form-field">
                <wow-input
                  type="datetime-local"
                  .value=${this.previewData.scheduledDateTime?.substring(0, 16) ?? ""}
                  @input=${this.handleScheduleDateTimeChange}
                ></wow-input>
              </div>
            </expandible-switch>

            <!-- Set as Cover Switch -->
            <div class="switch-section">
              <wow-switch
                .checked=${!!(this.previewData.postId && this.previewData.isCurrentlyCover)}
                @change=${this.handleSetCoverChange}
                label="${t("previewPublish.pinHighlight")}"
                .helpText=${t("previewPublish.pinHighlightSubtitle")}
              ></wow-switch>
              ${this.previewData.postId && this.previewData.isCurrentlyCover
                ? html`
                    <div class="cover-status-alert">
                      <alert-item message="${t("previewPublish.postSetAsCover")}" severity="info"></alert-item>
                    </div>
                  `
                : ""}
            </div>
          </div>
        </div>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "preview-publish-modal": PreviewPublishModal;
  }
}
